
import React from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { PERMISSIONS } from "@/shared/constants/permissions";
import type { AppLayoutProps } from "./types";

// Lazy load system features
const DeviceDetailPage = React.lazy(() => import("@/features/system/pages/DeviceDetailPage"));
const LocationsPage = React.lazy(() => import("@/features/system/pages/locations/LocationsPage"));
const LocationsDetailspage = React.lazy(
  () => import("@/features/system/pages/locations/LocationsDetailspage")
);
const LocationHistoryPage = React.lazy(
  () => import("@/features/system/pages/locations/LocationHistoryPage")
);
const StreetSegmentsPage = React.lazy(
  () => import("@/features/system/pages/locations/StreetSegments")
);
const StreetSegmentsDetailsPage = React.lazy(
  () => import("@/features/system/pages/locations/StreetSegmentsDetailsPage")
);
const Devices = React.lazy(() => import("@/app/pages/System/Devices"));
const DeviceMdt = React.lazy(() => import("@/app/pages/System/DeviceMdt"));
const MessageTemplates = React.lazy(() => import("@/app/pages/System/MessageTemplates"));
const Filterset = React.lazy(() => import("@/app/pages/System/Filterset"));
const UserEventsPage = React.lazy(
  () => import("@/features/system/components/userEvents/UserEventsPage")
);
const ScheduleEventsPage = React.lazy(
  () => import("@/features/system/components/scheduleEvents/ScheduleEventsPage")
);
const TripEventsPage = React.lazy(
  () => import("@/features/system/components/tripEvents/TripEventsPage")
);
const SubscriptionEventsPage = React.lazy(
  () => import("@/features/system/components/subscriptionEvents/SubscriptionEventsPage")
);
const VehicleEventsPage = React.lazy(
  () => import("@/features/system/components/vehicleEvents/VehicleEventsPage")
);
const ReimbursementEventsPage = React.lazy(
  () => import("@/features/system/components/reimbursementEvents/ReimbursementEventsPage")
);
const BatchJobEventsPage = React.lazy(
  () => import("@/features/system/components/batchJobEvents/BatchJobEventsPage")
);
const ConfigurationParametersPage = React.lazy(
  () => import("@/features/system/components/configurationParameters/ConfigurationParametersPage")
);
const CustomValuesPage = React.lazy(
  () => import("@/features/system/components/customValues/CustomValuesPage")
);
const ScheduledCustomValuesPage = React.lazy(
  () => import("@/features/system/components/scheduledCustomValues/ScheduledCustomValuesPage")
);
const ReportTemplatesPage = React.lazy(
  () => import("@/features/system/components/reportTemplates/ReportTemplatesPage")
);
const BatchJobPage = React.lazy(
  () => import("@/features/system/components/batchJobs/BatchJobPage")
);
const BatchJobDetailsPage = React.lazy(
  () => import("@/features/system/components/batchJobs/details/BatchJobDetailsPage")
);
const BatchJobDetailsTab = React.lazy(
  () => import("@/features/system/components/batchJobs/details/BatchJobDetailsTab")
);
const BatchJobEventsTab = React.lazy(
  () => import("@/features/system/components/batchJobs/details/BatchJobEventsTab")
);
const ServiceAreasPage = React.lazy(
  () => import("@/features/system/pages/service_areas/ServiceAreasPage")
);
const ServiceAreaDetails = React.lazy(
  () => import("@/features/system/pages/service_areas/ServiceAreaDetails")
);
const ServiceAreaHistoryPage = React.lazy(
  () => import("@/features/system/pages/service_areas/ServiceAreaHistoryPage")
);
const PolygonsPage = React.lazy(() => import("@/features/system/pages/polygons/PolygonsPage"));
const PolygonsDetails = React.lazy(
  () => import("@/features/system/pages/polygons/PolygonsDetails")
);
const PolygonsHistory = React.lazy(
  () => import("@/features/system/pages/polygons/PolygonsHistory")
);
const RequiredFieldsPage = React.lazy(() => import("@/features/system/pages/RequiredFieldsPage"));
const UsersPage = React.lazy(
  () => import("@/features/system/pages/userManagement/UsersPage")
);
const UserDetailsPage = React.lazy(
  () => import("@/features/system/pages/userManagement/UserDetailsPage")
);
const UserRolesPage = React.lazy(
  () => import("@/features/system/pages/userManagement/UserRolesPage")
);
const UserRoleDetailsPage = React.lazy(
  () => import("@/features/system/pages/userManagement/UserRoleDetailsPage")
);
const UserRoleEditPage = React.lazy(
  () => import("@/features/system/pages/userManagement/UserRoleEditPage")
);
const NewUserRolePage = React.lazy(
  () => import("@/features/system/pages/userManagement/NewUserRolePage")
);
const CentersPage = React.lazy(
  () => import("@/features/system/pages/userManagement/CentersPage")
);
const CenterDetailsPage = React.lazy(
  () => import("@/features/system/pages/userManagement/CenterDetailsPage")
);
const UserPreferencesPage = React.lazy(
  () => import("@/features/system/pages/userManagement/UserPreferencesPage")
);

export const systemRoutes = (AppLayout: React.FC<AppLayoutProps>): RouteObject[] => [
  {
    path: "/system/locations",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.LOCATION_VIEW}>
          <LocationsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/locations/:locationId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.LOCATION_VIEW}>
          <LocationsDetailspage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/locations/:locationId/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.LOCATION_UPDATE}>
          <LocationHistoryPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/street-segments",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.LOCATION_VIEW}>
          <StreetSegmentsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/street-segments/:segmentId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.LOCATION_VIEW}>
          <StreetSegmentsDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/devices",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DEVICE_VIEW}>
          <Devices />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/devices/mdt-devices",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.MDT_DEVICE_VIEW}>
          <DeviceMdt />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/devices/:deviceId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DEVICE_VIEW}>
          <DeviceDetailPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/message-templates",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.CANNED_MESSAGE_VIEW}>
          <MessageTemplates />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/filterset",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_FILTER_VIEW}>
          <Filterset />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/service-areas",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SERVICE_AREA_VIEW}>
          <ServiceAreasPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/service-areas/:serviceAreaId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SERVICE_AREA_VIEW}>
          <ServiceAreaDetails />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/service-areas/:serviceAreaId/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SERVICE_AREA_UPDATE}>
          <ServiceAreaHistoryPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/polygons",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ENCLOSURE_VIEW}>
          <PolygonsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/polygons/:polygonId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ENCLOSURE_VIEW}>
          <PolygonsDetails />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/polygons/:polygonId/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ENCLOSURE_UPDATE}>
          <PolygonsHistory />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/user-events",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_EVENT_VIEW}>
          <UserEventsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/schedule-events",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ROUTE_EVENT_VIEW}>
          <ScheduleEventsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/trip-events",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_EVENT_VIEW}>
          <TripEventsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/subscription-events",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SUBSCRIPTION_VIEW}>
          <SubscriptionEventsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/vehicle-events",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_VEHICLE_EVENT_VIEW}>
          <VehicleEventsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/reimbursement-events",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.REIMBURSEMENT_VIEW}>
          <ReimbursementEventsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/batch-job-events",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.BATCH_JOB_VIEW}>
          <BatchJobEventsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/configuration",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.CONFIGURATION_VIEW}>
          <ConfigurationParametersPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/custom-values",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ALLOWED_VALUE_SET_VIEW}>
          <CustomValuesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/schedule-custom-fields",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.METAROUTE_CUSTOM_FIELD_VIEW}>
          <ScheduledCustomValuesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/report-templates",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.REPORT_TEMPLATE_VIEW}>
          <ReportTemplatesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/batch-jobs",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.BATCH_JOB_VIEW}>
          <BatchJobPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/required-fields",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.REQUIRED_FIELD_VIEW}>
          <RequiredFieldsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/batch-jobs/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.BATCH_JOB_VIEW}>
          <BatchJobDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
    children: [
      {
        path: "details",
        element: (
        <ProtectedRoute apiPermission={PERMISSIONS.BATCH_JOB_VIEW}>
        <BatchJobDetailsTab />
        </ProtectedRoute>
        ),
      },
      {
        path: "events",
        element: (
        <ProtectedRoute apiPermission={PERMISSIONS.BATCH_JOB_VIEW}>
        <BatchJobEventsTab />
        </ProtectedRoute>
        ),
      },
      {
        path: "history",
        element: (
          <ProtectedRoute apiPermission={PERMISSIONS.BATCH_JOB_UPDATE}>
            <></>
          </ProtectedRoute>
        ),
      },
      {
        path: "",
        element: <Navigate to="details" replace />,
      },
    ],
  },
  {
    path: "/system/users",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_VIEW}>
          <UsersPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/users/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_VIEW}>
          <UserDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/user-roles",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_ROLE_VIEW}>
          <UserRolesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/user-roles/new",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_ROLE_CREATE}>
          <NewUserRolePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/user-roles/:roleId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_ROLE_VIEW}>
          <UserRoleDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/user-roles/:roleId/edit",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_ROLE_UPDATE}>
          <UserRoleEditPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/centers",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.CLIENT_POOL_VIEW}>
          <CentersPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/centers/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.CLIENT_POOL_VIEW}>
          <CenterDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/system/user-preferences",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_UPDATE} allowedUserTypes={[4]}>
          <UserPreferencesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
];
